// Regras de negócio de contas a pagar (recorrentes e únicas).
// Funções puras sobre datas em string 'YYYY-MM-DD' e mes 0-11, para evitar
// os problemas de fuso horário do Date com strings ISO.

export type StatusOcorrencia = 'paga' | 'sem_cobranca' | 'atrasada' | 'vence_hoje' | 'a_vencer';

export interface ContaPagar {
  id: string;
  descricao: string;
  valor: number;
  recorrente: boolean;
  dia_vencimento: number | null;
  data_vencimento: string | null; // YYYY-MM-DD (apenas contas únicas)
  categoria_id: string | null;
  banco_id: string | null;
  ativo: boolean;
  criado_em?: string | null;
}

export interface PagamentoConta {
  id?: string;
  conta_id: string;
  mes: number; // 0-11
  ano: number;
  valor_pago?: number;
  lancamento_id?: string | null;
}

export interface Ocorrencia {
  conta: ContaPagar;
  mes: number;
  ano: number;
  dataVencimento: string;
  paga: boolean;
  // Mês sem cobrança: a ocorrência está resolvida sem ter gerado despesa
  semCobranca: boolean;
  status: StatusOcorrencia;
  pagamento?: PagamentoConta;
}

// Baixa de "não veio cobrança neste mês": valor zero e nenhum lançamento.
// Diferente de um pagamento, que sempre tem despesa por trás
export function ehSemCobranca(pagamento?: PagamentoConta): boolean {
  return !!pagamento && Number(pagamento.valor_pago || 0) === 0 && !pagamento.lancamento_id;
}

export function ultimoDiaDoMes(mes: number, ano: number): number {
  return new Date(ano, mes + 1, 0).getDate();
}

export function formatarDataISO(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

export function dataHojeLocal(): string {
  const d = new Date();
  return formatarDataISO(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDias(dataISO: string, dias: number): string {
  const [ano, mes, dia] = dataISO.split('-').map(Number);
  const d = new Date(ano, mes - 1, dia + dias);
  return formatarDataISO(d.getFullYear(), d.getMonth(), d.getDate());
}

export function diasAte(deISO: string, ateISO: string): number {
  const [a1, m1, d1] = deISO.split('-').map(Number);
  const [a2, m2, d2] = ateISO.split('-').map(Number);
  return Math.round((new Date(a2, m2 - 1, d2).getTime() - new Date(a1, m1 - 1, d1).getTime()) / 86400000);
}

// Data de vencimento da conta no mês/ano indicado, ou null se não há ocorrência.
// Recorrente: o dia é limitado ao último dia do mês (venc. 31 em fevereiro → 28/29)
// e a recorrência só existe a partir do mês em que a conta foi criada.
// Única: só existe no mês/ano da própria data de vencimento.
export function dataVencimentoNoMes(conta: ContaPagar, mes: number, ano: number): string | null {
  if (conta.recorrente) {
    if (!conta.dia_vencimento) return null;
    const dia = Math.min(conta.dia_vencimento, ultimoDiaDoMes(mes, ano));
    if (conta.criado_em) {
      // Converte o timestamptz para data LOCAL (slice da string ISO usaria UTC e
      // deslocaria o mês para contas criadas à noite no fuso do Brasil)
      const criado = new Date(conta.criado_em);
      const cAno = criado.getFullYear();
      const cMes = criado.getMonth();
      if (ano < cAno || (ano === cAno && mes < cMes)) return null;
      // No mês de criação, vencimento que já passou não gera ocorrência:
      // quem cadastra "Internet dia 5" no dia 20 já pagou a de julho por fora,
      // então a primeira ocorrência é a do mês seguinte
      if (ano === cAno && mes === cMes && dia < criado.getDate()) return null;
    }
    return formatarDataISO(ano, mes, dia);
  }
  if (!conta.data_vencimento) return null;
  const [vAno, vMes] = conta.data_vencimento.split('-').map(Number);
  return vAno === ano && vMes - 1 === mes ? conta.data_vencimento : null;
}

export function statusOcorrencia(
  dataVencimento: string,
  paga: boolean,
  hoje: string,
  semCobranca: boolean = false
): StatusOcorrencia {
  if (paga) return semCobranca ? 'sem_cobranca' : 'paga';
  if (dataVencimento < hoje) return 'atrasada';
  if (dataVencimento === hoje) return 'vence_hoje';
  return 'a_vencer';
}

// Todas as ocorrências de contas ativas no mês/ano, ordenadas por vencimento
export function ocorrenciasDoMes(
  contas: ContaPagar[],
  pagamentos: PagamentoConta[],
  mes: number,
  ano: number,
  hoje: string
): Ocorrencia[] {
  const out: Ocorrencia[] = [];
  for (const conta of contas) {
    if (conta.ativo === false) continue;
    const dataVencimento = dataVencimentoNoMes(conta, mes, ano);
    if (!dataVencimento) continue;
    // Conta única tem no máximo uma ocorrência, então qualquer pagamento dela conta:
    // se o usuário editar a data para outro mês depois de pagar, ela continua paga
    // em vez de reaparecer em aberto e permitir pagamento duplicado
    const pagamento = conta.recorrente
      ? pagamentos.find(p => p.conta_id === conta.id && p.mes === mes && p.ano === ano)
      : pagamentos.find(p => p.conta_id === conta.id);
    const semCobranca = ehSemCobranca(pagamento);
    out.push({
      conta,
      mes,
      ano,
      dataVencimento,
      paga: !!pagamento,
      semCobranca,
      status: statusOcorrencia(dataVencimento, !!pagamento, hoje, semCobranca),
      pagamento,
    });
  }
  out.sort((a, b) => a.dataVencimento.localeCompare(b.dataVencimento));
  return out;
}

// Pendências para lembrete: atrasadas (até 12 meses para trás) e as que vencem
// nos próximos `dias` dias (incluindo ocorrências do mês seguinte).
export function ocorrenciasPendentes(
  contas: ContaPagar[],
  pagamentos: PagamentoConta[],
  hoje: string,
  dias: number = 7
): Ocorrencia[] {
  const [hAno, hMes] = hoje.split('-').map(Number);
  const limite = addDias(hoje, dias);
  const out: Ocorrencia[] = [];
  for (let offset = -12; offset <= 1; offset++) {
    let m = hMes - 1 + offset;
    const a = hAno + Math.floor(m / 12);
    m = ((m % 12) + 12) % 12;
    for (const oc of ocorrenciasDoMes(contas, pagamentos, m, a, hoje)) {
      if (!oc.paga && oc.dataVencimento <= limite) out.push(oc);
    }
  }
  out.sort((a, b) => a.dataVencimento.localeCompare(b.dataVencimento));
  return out;
}
